/**
 * Price without IVA (RS Components style).
 *
 * 1. Variant changes: the theme's updatePrices() (theme.js) rewrites [data-price]
 *    with variant.price. A MutationObserver on that element looks up the selected
 *    variant id in the block's data-iva-variants map ({id: {n, g, c}} in cents,
 *    rendered by snippets/product-price.liquid) and rewrites the computed figure:
 *    [data-iva-net] when catalogue prices include IVA, [data-iva-gross] /
 *    [data-iva-gross-compare] when they don't (then [data-price] is the price without IVA).
 *    The formatted money string is never parsed.
 * 2. Tooltip for the red "i" button (snippets/iva-info.liquid): opens on hover and
 *    focus, toggles on tap/click, closes on Escape or an outside click. It is shown
 *    in a single fixed element on <body> so card/slider overflow can't clip it.
 */
(function () {
  "use strict";

  if (window.ivaPriceLoaded) return;
  window.ivaPriceLoaded = true;

  var SELECTORS = {
    block: "[data-iva-price]",
    net: "[data-iva-net]",
    gross: "[data-iva-gross]",
    grossCompare: "[data-iva-gross-compare]",
    price: "[data-price]",
    button: "[data-iva-info]",
    tip: "[data-iva-tip]",
    variantSelect: "select[data-variant-select], [data-variant-select]",
    idInput: '[name="id"]'
  };

  /* ------------------------------------------------------------------------
   * Money formatting (same placeholders as the theme's formatMoney)
   * --------------------------------------------------------------------- */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var number = (cents / 100).toFixed(precision);
    var parts = number.split(".");
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    return whole + (parts[1] ? decimal + parts[1] : "");
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || "";
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var value;

    switch (match && match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2, ",", ".");
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_with_period_and_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      default:
        return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(cents / 100);
    }

    return format.replace(placeholder, value);
  }

  /* ------------------------------------------------------------------------
   * Variant change -> price without IVA
   * --------------------------------------------------------------------- */
  function getUnavailableString() {
    try {
      return window.theme.strings.products.product.unavailable;
    } catch (e) {
      return null;
    }
  }

  function getSelectedVariantId(block) {
    var container =
      block.closest('[data-section-type="product"]') ||
      block.closest("form") ||
      block.closest(".shopify-section");
    if (!container) return null;

    var input = container.querySelector(SELECTORS.variantSelect) || container.querySelector(SELECTORS.idInput);
    return input ? input.value : null;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function updateBlock(block) {
    var priceEl = block.querySelector(SELECTORS.price);
    if (!priceEl) return;

    var unavailable = getUnavailableString();
    if (unavailable && priceEl.textContent.trim() === unavailable.trim()) {
      block.setAttribute("data-iva-unavailable", "");
      return;
    }
    block.removeAttribute("data-iva-unavailable");

    if (!block.ivaVariants) {
      try {
        block.ivaVariants = JSON.parse(block.getAttribute("data-iva-variants") || "{}");
      } catch (e) {
        block.ivaVariants = {};
      }
    }

    var id = getSelectedVariantId(block);
    var prices = id ? block.ivaVariants[id] : undefined;
    if (!prices || typeof prices.n !== "number") return;

    // Prices stored with IVA: only the price without IVA is ours to write.
    // Prices stored without IVA: the price with IVA (and its compare-at) are ours.
    setText(block.querySelector(SELECTORS.net), formatMoney(prices.n));
    setText(block.querySelector(SELECTORS.gross), formatMoney(prices.g));
    setText(block.querySelector(SELECTORS.grossCompare), prices.c > prices.g ? formatMoney(prices.c) : "");
  }

  function initBlock(block) {
    if (block.ivaObserved) return;
    var priceEl = block.querySelector(SELECTORS.price);
    if (!priceEl) return;
    block.ivaObserved = true;

    new MutationObserver(function () {
      updateBlock(block);
    }).observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
   * Tooltip ids must be unique (the same product can appear twice on a page)
   * --------------------------------------------------------------------- */
  var uid = 0;
  function initButton(button) {
    if (button.ivaReady) return;
    button.ivaReady = true;

    var tip = button.parentNode && button.parentNode.querySelector(SELECTORS.tip);
    if (!tip) return;

    var current = tip.id;
    if (!current || document.querySelectorAll('[id="' + current + '"]').length > 1) {
      uid += 1;
      tip.id = "iva-tip-" + uid + "-" + Math.random().toString(36).slice(2, 7);
      button.setAttribute("aria-describedby", tip.id);
    }
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTORS.block)) initBlock(root);
    if (root.matches && root.matches(SELECTORS.button)) initButton(root);
    Array.prototype.forEach.call(root.querySelectorAll(SELECTORS.block), initBlock);
    Array.prototype.forEach.call(root.querySelectorAll(SELECTORS.button), initButton);
  }

  /* ------------------------------------------------------------------------
   * Tooltip behaviour
   * --------------------------------------------------------------------- */
  var tooltip = null;
  var activeButton = null;
  var pinned = false;
  var closeTimer = null;
  var MARGIN = 8;

  function getTooltip() {
    if (tooltip) return tooltip;
    tooltip = document.createElement("div");
    tooltip.className = "iva-tooltip";
    tooltip.hidden = true;
    // The button is already described by its own (hidden) tip via aria-describedby,
    // so this visual copy is hidden from assistive tech to avoid reading it twice.
    tooltip.setAttribute("aria-hidden", "true");
    tooltip.addEventListener("mouseenter", cancelClose);
    tooltip.addEventListener("mouseleave", scheduleClose);
    document.body.appendChild(tooltip);
    return tooltip;
  }

  function position() {
    if (!activeButton || !tooltip || tooltip.hidden) return;

    var rect = activeButton.getBoundingClientRect();
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var vh = window.innerHeight;
    var width = tooltip.offsetWidth;
    var height = tooltip.offsetHeight;
    var centerX = rect.left + rect.width / 2;

    var left = Math.min(Math.max(centerX - width / 2, MARGIN), Math.max(vw - width - MARGIN, MARGIN));
    var placement = "bottom";
    var top = rect.bottom + 10;

    if (top + height > vh - MARGIN && rect.top - 10 - height >= MARGIN) {
      placement = "top";
      top = rect.top - 10 - height;
    }

    tooltip.style.left = Math.round(left) + "px";
    tooltip.style.top = Math.round(top) + "px";
    tooltip.style.setProperty("--iva-arrow-x", Math.round(Math.min(Math.max(centerX - left, 12), width - 12)) + "px");
    tooltip.setAttribute("data-placement", placement);
  }

  function open(button, pin) {
    cancelClose();
    var tip = button.parentNode && button.parentNode.querySelector(SELECTORS.tip);
    if (!tip) return;

    if (activeButton && activeButton !== button) {
      activeButton.setAttribute("aria-expanded", "false");
    }

    var el = getTooltip();
    if (activeButton !== button || el.hidden) {
      el.innerHTML = tip.innerHTML;
    }
    activeButton = button;
    pinned = Boolean(pin);
    button.setAttribute("aria-expanded", "true");
    el.hidden = false;
    position();
  }

  function close() {
    cancelClose();
    if (activeButton) activeButton.setAttribute("aria-expanded", "false");
    activeButton = null;
    pinned = false;
    if (tooltip) tooltip.hidden = true;
  }

  function scheduleClose() {
    if (pinned) return;
    cancelClose();
    closeTimer = setTimeout(close, 150);
  }

  function cancelClose() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function buttonFrom(target) {
    return target && target.closest ? target.closest(SELECTORS.button) : null;
  }

  // Capture phase so the card link, quick view and other theme handlers never see the click.
  document.addEventListener(
    "click",
    function (event) {
      var button = buttonFrom(event.target);

      if (button) {
        event.preventDefault();
        event.stopPropagation();

        if (activeButton === button && pinned) {
          close();
        } else {
          open(button, true);
        }
        return;
      }

      if (activeButton && !(tooltip && tooltip.contains(event.target))) {
        close();
      }
    },
    true
  );

  document.addEventListener("mouseover", function (event) {
    var button = buttonFrom(event.target);
    if (button) {
      if (activeButton !== button) open(button, false);
      else cancelClose();
    }
  });

  document.addEventListener("mouseout", function (event) {
    var button = buttonFrom(event.target);
    if (!button || button !== activeButton) return;
    if (event.relatedTarget && button.contains(event.relatedTarget)) return;
    scheduleClose();
  });

  document.addEventListener("focusin", function (event) {
    var button = buttonFrom(event.target);
    if (button && activeButton !== button) open(button, false);
  });

  document.addEventListener("focusout", function (event) {
    var button = buttonFrom(event.target);
    if (!button || button !== activeButton) return;
    if (event.relatedTarget && tooltip && tooltip.contains(event.relatedTarget)) return;
    // Keep a tooltip opened by mouse hover/click while the pointer is still on it.
    if (button.matches(":hover")) return;
    close();
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && activeButton) close();
  });

  var rafId = null;
  function onViewportChange() {
    if (!activeButton) return;
    if (!activeButton.isConnected) {
      close();
      return;
    }
    if (rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = null;
      position();
    });
  }

  window.addEventListener("scroll", onViewportChange, { capture: true, passive: true });
  window.addEventListener("resize", onViewportChange, { passive: true });

  /* ------------------------------------------------------------------------
   * Init (also covers quick view / quick add content injected later)
   * --------------------------------------------------------------------- */
  function init() {
    scan(document);

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scan(added[j]);
        }
      }
      if (activeButton && !activeButton.isConnected) close();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
